import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { FixedSizeList as List } from 'react-window';

import SimpleAthleteItem from '../components/presentational/SimpleAthleteItem';

// Add a styled wrapper for the List component with custom scrollbar styles
const StyledList = styled(List)`
  /* Hide the default scrollbar track */
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  /* Style the scrollbar thumb */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  /* Style the scrollbar thumb */
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.scrollbarThumb};
    border-radius: 3px;
  }
  
  /* For Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.scrollbarThumb} transparent;
`;

/**
 * @param {Object} data an object that's passed in from the 'itemData' prop in the list component
 * seen below
 * @param {Integer} index the index of the current data object in the passed array
 * @param {Object} style necessary object that styles each individual container in accordance with
 * the requirements of the list
 * @returns a formatted list of dynamically rendered athlete rows with checkboxes
 */
const AthleteRenderer = ({
  data, index, style,
}) => {
  const {
    list, checkedAthletes, onCheckboxChange,
  } = data;
  const athlete = list[index];

  return (
    <div style={style}>
      <SimpleAthleteItem
        athlete={athlete}
        isChecked={checkedAthletes.has(athlete.id)}
        onCheckboxChange={onCheckboxChange}
      />
    </div>
  );
};

/**
 * SimpleCalendarSelectAthleteList - A virtualized list of athletes with checkbox selection
 *
 * @param {Array} calendarSelectAthletes the list of athletes to be passed into a generated windowed list
 * @param {Boolean} isUserSearching whether the user is currently searching
 * @param {Array} searchedAthleteList the filtered list of athletes based on search
 * @param {Set} checkedAthletes Set of checked athlete IDs
 * @param {Function} onCheckboxChange callback when checkbox state changes
 * @returns SimpleCalendarSelectAthleteList component with virtualized scrolling and checkbox selection
 */
const SimpleCalendarSelectAthleteList = ({
  calendarSelectAthletes,
  isUserSearching,
  searchedAthleteList,
  checkedAthletes,
  onCheckboxChange,
}) => {
  const listMaker = () => {
    if (isUserSearching) {
      if (searchedAthleteList.length) {
        return searchedAthleteList;
      }
    }
    return calendarSelectAthletes;
  };

  return (
    <StyledList
      height={300}
      itemData={{
        list: listMaker(),
        checkedAthletes,
        onCheckboxChange,
        isUserSearching,
      }}
      itemCount={!isUserSearching ? calendarSelectAthletes.length : searchedAthleteList.length}
      itemSize={36}
      width='100%'
      className='fixed-list'
    >
      {AthleteRenderer}
    </StyledList>
  );
};

AthleteRenderer.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  index: PropTypes.number.isRequired,
  style: PropTypes.instanceOf(Object).isRequired,
};

SimpleCalendarSelectAthleteList.propTypes = {
  calendarSelectAthletes: PropTypes.instanceOf(Array).isRequired,
  isUserSearching: PropTypes.bool.isRequired,
  searchedAthleteList: PropTypes.instanceOf(Array).isRequired,
  checkedAthletes: PropTypes.instanceOf(Set).isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
};

export default SimpleCalendarSelectAthleteList;
